import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";

const DEFAULT_COOKIES = {
  necessary: true,
  performance: false,
  functional: false,
  advertising: false,
};

export default function CookiePreferences({ t = (k) => k, p = {}, theme = "light" }) {
  const [preferences, setPreferences] = useState(DEFAULT_COOKIES);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("cookiePreferences");
    if (stored) {
      try {
        setPreferences({ ...DEFAULT_COOKIES, ...JSON.parse(stored) });
      } catch (e) {
        console.error("Failed to parse cookie preferences", e);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem("cookiePreferences", JSON.stringify(prefs));
    setPreferences(prefs);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      performance: true,
      functional: true,
      advertising: true,
    };
    savePreferences(allAccepted);
  };

  const rejectAll = () => {
    const allRejected = {
      necessary: true,
      performance: false,
      functional: false,
      advertising: false,
    };
    savePreferences(allRejected);
  };

  const togglePreference = (key) => {
    if (key === "necessary") return;
    const updated = { ...preferences, [key]: !preferences[key] };
    savePreferences(updated);
  };

  const cookieCategories = [
    {
      key: "necessary",
      title: t("cookies.necessary"),
      description: t("cookies.necessary_desc"),
      alwaysActive: true,
    },
    {
      key: "performance",
      title: t("cookies.performance"),
      description: t("cookies.performance_desc"),
      alwaysActive: false,
    },
    {
      key: "functional",
      title: t("cookies.functional"),
      description: t("cookies.functional_desc"),
      alwaysActive: false,
    },
    {
      key: "advertising",
      title: t("cookies.advertising"),
      description: t("cookies.advertising_desc"),
      alwaysActive: false,
    },
  ];

  return (
    <div className={p.page}>
      <Breadcrumb
        items={[
          { label: t("nav.home"), href: "/" },
          { label: t("cookies.title") },
        ]}
        t={t}
        p={p}
      />

      <Container>
        <div className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <h1 className={`text-3xl md:text-4xl font-bold mb-6 ${p.h1 || ""}`}>
              {t("cookies.title")}
            </h1>

            <div className={`rounded-2xl p-6 md:p-8 mb-8 ${p.card || "bg-white"}`}>
              <p className={`text-base leading-relaxed mb-6 ${p.cardP || ""}`}>
                {t("cookies.intro")}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <button
                  onClick={acceptAll}
                  className="flex-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 px-6 py-3 text-sm font-semibold shadow-sm transition-colors"
                >
                  {t("cookies.accept_all")}
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 rounded-lg ring-1 ring-indigo-200 dark:ring-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 px-6 py-3 text-sm font-semibold transition-colors"
                >
                  {t("cookies.reject_all")}
                </button>
              </div>

              {saved && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 flex items-center gap-2">
                  <Check size={20} className="text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-800 dark:text-green-200 font-medium">
                    {t("cookies.saved")}
                  </span>
                </div>
              )}

              <div>
                <h2 className={`text-xl font-semibold mb-4 ${p.cardTitle || ""}`}>
                  {t("cookies.preferences")}
                </h2>

                <div className="space-y-4">
                  {cookieCategories.map((category) => (
                    <div
                      key={category.key}
                      className={`rounded-xl p-4 border ${
                        theme === "dark"
                          ? "border-white/10 bg-white/5"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className={`font-semibold mb-1 ${p.cardTitle || ""}`}>
                            {category.title}
                          </h3>
                          <p className={`text-sm opacity-80 ${p.cardP || ""}`}>
                            {category.description}
                          </p>
                          {category.alwaysActive && (
                            <span className="inline-block mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                              {t("cookies.always_active")}
                            </span>
                          )}
                        </div>

                        <div>
                          {category.alwaysActive ? (
                            <div className="w-12 h-6 rounded-full bg-indigo-600 flex items-center justify-end px-1">
                              <div className="w-4 h-4 rounded-full bg-white"></div>
                            </div>
                          ) : (
                            <button
                              onClick={() => togglePreference(category.key)}
                              className={`w-12 h-6 rounded-full transition-colors ${
                                preferences[category.key]
                                  ? "bg-indigo-600"
                                  : theme === "dark"
                                  ? "bg-gray-700"
                                  : "bg-gray-300"
                              } flex items-center ${
                                preferences[category.key] ? "justify-end" : "justify-start"
                              } px-1`}
                              aria-label={`Toggle ${category.title}`}
                            >
                              <div className="w-4 h-4 rounded-full bg-white shadow-sm"></div>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-black/5 dark:border-white/10">
                <p className={`text-sm opacity-70 ${p.cardP || ""}`}>
                  {t("cookies.footer_note")}{" "}
                  <Link
                    to="/privacy"
                    className="underline underline-offset-2 hover:no-underline text-indigo-600 dark:text-indigo-400"
                  >
                    {t("cookies.privacy_link")}
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
